import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { RailItem } from './workflowNav';
import { useProjectWorkflow } from '../../contexts/ProjectWorkflowContext';
import { clearPreferDashboard } from '../../utils/homeLaunch';
import { preloadRoute } from '../common/lazyRoute';

interface IconRailProps {
  items: RailItem[];
  disabled?: boolean;
}

const IconRail: React.FC<IconRailProps> = ({ items, disabled = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearProject, setNavTarget, setPanelOpen, requestHomeLaunch } = useProjectWorkflow();

  const handleNav = (item: RailItem) => {
    if (disabled && item.id !== 'home' && item.id !== 'clients') return;
    setNavTarget(item.id);

    // Home: open Clients & Projects panel and launch top project → Explore Sites
    if (item.id === 'home') {
      clearPreferDashboard();
      requestHomeLaunch();
      return;
    }

    // All other menus: keep the sub-sidebar closed
    clearProject();
    setPanelOpen(false);
    navigate(item.path);
  };

  const isActive = (item: RailItem) => {
    const sp = new URLSearchParams(location.search);
    const hasProjectContext = sp.has('projectId') && sp.get('standalone') !== '1';
    // Project workflow (Home) uses shared paths like /sites and /guest-blog-placements
    const inHomeWorkflow =
      location.pathname.includes('/project-workflow/') ||
      (hasProjectContext &&
        (location.pathname.startsWith('/sites') ||
          location.pathname.startsWith('/guest-blog-placements') ||
          location.pathname.startsWith('/planning')));

    if (item.id === 'home') {
      return location.pathname === '/dashboard' || inHomeWorkflow;
    }
    if (item.id === 'profile') return location.pathname.startsWith('/profile');
    // Global Orders / Sites / Planning: only when NOT inside a project workflow
    if (item.id === 'placements') {
      return location.pathname.startsWith('/guest-blog-placements') && !hasProjectContext;
    }
    if (item.id === 'sites' || item.id === 'planning') {
      return location.pathname.startsWith(item.path) && !hasProjectContext;
    }
    return location.pathname.startsWith(item.path);
  };

  const mainItems = items.filter((i) => i.section !== 'bottom');
  const bottomItems = items.filter((i) => i.section === 'bottom');

  const renderBtn = (item: RailItem) => {
    const active = isActive(item);
    const isDisabled = disabled && item.id !== 'home' && item.id !== 'clients';
    return (
      <Tooltip key={item.id} title={item.tooltip || item.label} placement="right">
        <button
          type="button"
          disabled={isDisabled}
          onMouseEnter={() => preloadRoute(item.path)}
          onFocus={() => preloadRoute(item.path)}
          onClick={() => handleNav(item)}
          className={`wf-rail-btn${active ? ' on' : ''}`}
          style={{
            opacity: isDisabled ? 0.4 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <span className="wf-rail-icon">{item.icon}</span>
          <span className="wf-rail-label">{item.label}</span>
          {item.badge != null && item.badge > 0 && (
            <span className="wf-rail-badge">{item.badge > 99 ? '99+' : item.badge}</span>
          )}
        </button>
      </Tooltip>
    );
  };

  return (
    <aside className="wf-rail">
      <div className="wf-rail-logo" title="Link Management">
        <img src={`${process.env.PUBLIC_URL || ''}/favicon.png`} alt="Link Management" />
      </div>
      <div className="wf-rail-divider" />
      <div className="wf-rail-main">{mainItems.map(renderBtn)}</div>
      {bottomItems.length > 0 && (
        <div className="wf-rail-footer">
          <div className="wf-rail-divider" />
          <div className="wf-rail-bottom">{bottomItems.map(renderBtn)}</div>
        </div>
      )}
    </aside>
  );
};

export default IconRail;
